import { useState } from "react";

import { useLoomControlSurfaceContext } from "./LoomControlSurfaceContext";
import { LoomControlSurfacePanel } from "./LoomControlSurfacePanel";
import type { LoomControlSurfaceActuationMode } from "./loomWingControlSurfaceContract";

type GeometryModel = "analysis-hinge" | "integrated-deformable" | "circular-cove" | "custom-cove";

const MODELS: readonly { id: GeometryModel; label: string }[] = [
  { id: "analysis-hinge", label: "Panel hinge" },
  { id: "integrated-deformable", label: "Integrated flap (XFoil-style)" },
  { id: "circular-cove", label: "Circular cove · constant clearance" },
  { id: "custom-cove", label: "Custom cove" },
];

function Range({ label, max, min, onChange, testId, value }: {
  label: string; max: number; min: number; onChange(value: number): void; testId: string; value: number;
}) {
  return <label className="loom-task-range"><span>{label}<output>{value.toFixed(1)}</output></span>
    <input aria-label={label} data-testid={testId} max={max} min={min}
      onChange={(event) => onChange(Number(event.target.value))} step="1" type="range" value={value} />
  </label>;
}

export function LoomTaskControlEditor() {
  const [legacyOpen, setLegacyOpen] = useState(false);
  const controls = useLoomControlSurfaceContext();
  const control = controls?.selectedControl ?? null;
  const state = controls?.selectedState ?? null;
  if (!controls?.selectedWing) return null;
  if (!control || !state) return <section className="loom-task-control-empty">
    <h3>Control surfaces</h3>
    <p>Add a movable trailing-edge control to this wing.</p>
    <button data-testid="loom-add-control-surface" disabled={!controls.canAddControlSurface}
      onClick={() => void controls.addControlSurface()} type="button">New control</button>
    {controls.error ? <p role="alert">{controls.error}</p> : null}
  </section>;

  const updateSpan = (part: "start" | "end", value: number) => controls.replaceControlSurface({
    ...control, spanBand: { ...control.spanBand, [part]: value },
  });
  const geometryMessage = controls.evaluation.status === "checking"
    ? "Updating the fixed wing and movable control bodies…"
    : controls.evaluation.status === "adapted"
      ? "Server-evaluated local control cut ready · not saved"
      : controls.evaluation.status === "error"
        ? controls.acceptedGeometry ? "Update failed — showing the last accepted control cut"
          : "Geometry engine unavailable — the wing was not changed"
        : "Control geometry has not been evaluated";
  return <section className="loom-task-control-editor" data-testid="loom-task-control-editor">
    <header><span>Control surface</span><h3>{control.label}</h3></header>
    <div aria-label="Geometry model" className="loom-task-geometry-models" role="radiogroup">
      {MODELS.map((entry) => <button aria-checked="false" data-availability="unavailable"
        disabled key={entry.id} role="radio" type="button">{entry.label}</button>)}
    </div>
    <small data-model-state={controls.evaluation.status} data-testid="loom-control-geometry-availability"
      role={controls.evaluation.status === "error" ? "alert" : "status"}>
      {geometryMessage}. This is an ephemeral adapted section split, not a CAD Boolean or a saved Thread version. The four strict v2 models are not connected yet.
    </small>
    <fieldset><legend>Span</legend>
      <Range label="Span start" max={control.spanBand.end * 100 - 1} min={0}
        onChange={(value) => updateSpan("start", value / 100)} testId="loom-control-span-start"
        value={control.spanBand.start * 100} />
      <Range label="Span end" max={100} min={control.spanBand.start * 100 + 1}
        onChange={(value) => updateSpan("end", value / 100)} testId="loom-control-span-end"
        value={control.spanBand.end * 100} />
    </fieldset>
    <label className="loom-task-field"><span>Actuation</span><select aria-label="Actuation"
      onChange={(event) => controls.setActuationMode(event.target.value as LoomControlSurfaceActuationMode)}
      value={state.mode}><option value="independent">Independent</option>
      <option value="symmetric">Symmetric</option><option value="antisymmetric">Antisymmetric</option></select></label>
    {state.mode === "independent" ? <fieldset><legend>Deflection</legend>
      <Range label="Port deflection" max={control.limitsDeg.port.maximum} min={control.limitsDeg.port.minimum}
        onChange={(value) => controls.setIndependentDeflection("port", value)}
        testId="loom-control-port-deflection" value={state.portDeflectionDeg} />
      <Range label="Starboard deflection" max={control.limitsDeg.starboard.maximum}
        min={control.limitsDeg.starboard.minimum}
        onChange={(value) => controls.setIndependentDeflection("starboard", value)}
        testId="loom-control-starboard-deflection" value={state.starboardDeflectionDeg} />
    </fieldset> : <Range label="Deflection" max={Math.min(control.limitsDeg.port.maximum,
      control.limitsDeg.starboard.maximum)} min={Math.max(control.limitsDeg.port.minimum,
      control.limitsDeg.starboard.minimum)} onChange={(value) => controls.setCoupledDeflection(value)}
      testId="loom-control-coupled-deflection" value={state.starboardDeflectionDeg} />}
    <details data-technical-details open={legacyOpen}>
      <summary onClick={(event) => {
        event.preventDefault();
        setLegacyOpen((current) => !current);
      }}>Adapted control cut</summary>
      <p>The server builds complementary capped fixed/control meshes from these v1 inputs. The cove is sampled and concentric, not exact CAD or a strict circular-cove claim.</p>
      <LoomControlSurfacePanel />
    </details>
    <button onClick={() => void controls.addControlSurface()} type="button">New control</button>
    {controls.error ? <p role="alert">{controls.error}</p> : null}
  </section>;
}
